/**
 * Mock Training Center
 * Create and manage AI-powered mock conversations for recruiter training
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  BarChart3,
  Settings,
  Plus,
  AlertTriangle,
  Users,
  Trophy,
  Loader2,
  Play,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Phone,
  ChevronRight,
  Search,
  Filter,
  Download,
  Eye,
  Sparkles,
  Wand2,
  Brain,
  Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function MockTraining() {
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [incidentsLoaded, setIncidentsLoaded] = useState(false);
  
  // Assignment dialog states
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedScenarioForAssign, setSelectedScenarioForAssign] = useState(null);
  const [assigningLoading, setAssigningLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignmentDueDate, setAssignmentDueDate] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  // AI Generation states
  const [showAIGenerateDialog, setShowAIGenerateDialog] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiGenerateOptions, setAiGenerateOptions] = useState({
    num_scenarios: 3,
    categories: ['age', 'pregnancy', 'religion', 'disability', 'national_origin'],
    use_real_incidents: true
  });
  const [commonIssues, setCommonIssues] = useState([]);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [newScenario, setNewScenario] = useState({
    title: '',
    description: '',
    category: 'age',
    difficulty: 'medium',
    candidate_statement: '',
    ideal_response: ''
  });

  // Stats
  const [stats, setStats] = useState({
    total_scenarios: 0,
    active_assignments: 0,
    completed_sessions: 0,
    average_score: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch scenarios
      const scenariosRes = await api.get('/mock-training/scenarios');
      setScenarios(scenariosRes.data.scenarios || []);
      
      // Fetch stats
      const statsRes = await api.get('/mock-training/stats');
      setStats(statsRes.data || stats);
      
      // Fetch assignments
      try {
        const assignmentsRes = await api.get('/mock-training/assignments');
        setAssignments(assignmentsRes.data.assignments || []);
      } catch (e) {
        console.log('Assignments not available');
      }
      
      // Fetch team members for assignments
      try {
        const usersRes = await api.get('/users');
        const users = usersRes.data || [];
        // Filter to team members (exclude admins if needed)
        setTeamMembers(users.filter(u => u.role_id !== 'super_admin'));
      } catch (e) {
        console.log('Users not available');
      }
      
      // Fetch common issues for AI suggestions
      try {
        const issuesRes = await api.get('/mock-training/common-issues');
        setCommonIssues(issuesRes.data.issues || []);
      } catch (e) {
        console.log('Common issues not available');
      }
      
    } catch (error) {
      console.log('Mock training data not available:', error);
      // Use empty defaults
    } finally {
      setLoading(false);
    }
  };

  const loadIncidents = async () => {
    setLoadingIncidents(true);
    try {
      // Fetch challenging calls that could be used as training scenarios
      const response = await api.get('/calls/challenging', {
        params: { limit: 20 }
      });
      
      // Transform calls into incident format
      const callsData = response.data.calls || response.data || [];
      const formattedIncidents = callsData.map(call => ({
        id: call._id || call.id,
        call_id: call._id || call.id,
        title: call.candidate_name ? `Call with ${call.candidate_name}` : `Call ${(call._id || call.id).slice(-6)}`,
        date: call.created_at || call.date,
        score: call.overall_score || call.score || 0,
        category: call.call_type || 'general',
        violation_type: call.violations?.[0]?.type || call.compliance_issues?.[0] || 'compliance',
        description: call.summary || call.ai_feedback?.summary || 'Challenging call identified for training',
        transcript_excerpt: typeof call.transcript === 'string' 
          ? call.transcript.slice(0, 200) + '...'
          : (call.transcript?.[0]?.text || '').slice(0, 200) + '...',
        recruiter_name: call.user_name || call.recruiter_name || 'Unknown'
      }));
      
      setIncidents(formattedIncidents);
      setIncidentsLoaded(true);
      
      if (formattedIncidents.length === 0) {
        toast.info('No challenging calls found. Calls with low scores will appear here.');
      } else {
        toast.success(`Loaded ${formattedIncidents.length} challenging calls`);
      }
    } catch (error) {
      console.error('Failed to load incidents:', error);
      toast.error('Failed to load incidents. Make sure the API is available.');
      setIncidents([]);
    } finally {
      setLoadingIncidents(false);
    }
  };

  const createScenario = async () => {
    try {
      const response = await api.post('/mock-training/scenarios', newScenario);
      setScenarios([...scenarios, response.data]);
      setShowCreateDialog(false);
      setNewScenario({
        title: '',
        description: '',
        category: 'age',
        difficulty: 'medium',
        candidate_statement: '',
        ideal_response: ''
      });
      toast.success('Scenario created successfully');
      // Update stats
      setStats(prev => ({ ...prev, total_scenarios: prev.total_scenarios + 1 }));
    } catch (error) {
      toast.error('Failed to create scenario');
    }
  };

  const generateAIScenarios = async () => {
    setGeneratingAI(true);
    try {
      const response = await api.post('/mock-training/generate-ai-scenarios', aiGenerateOptions);
      
      if (response.data.success) {
        const newScenarios = response.data.scenarios || [];
        setScenarios([...scenarios, ...newScenarios]);
        toast.success(`Generated ${response.data.generated_count} scenarios using AI!`);
        setShowAIGenerateDialog(false);
        // Update stats
        setStats(prev => ({ 
          ...prev, 
          total_scenarios: prev.total_scenarios + response.data.generated_count 
        }));
      } else {
        toast.error('AI generation failed');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate scenarios with AI');
    } finally {
      setGeneratingAI(false);
    }
  };

  const createScenarioFromIncident = async (incident) => {
    try {
      const scenarioData = {
        title: `Training: ${incident.title}`,
        description: `Created from real call incident. ${incident.description}`,
        category: incident.category || 'compliance',
        difficulty: incident.score < 5 ? 'hard' : incident.score < 7 ? 'medium' : 'easy',
        source_call_id: incident.call_id,
        candidate_statement: incident.transcript_excerpt,
        ideal_response: 'Review the original call for guidance on proper handling.'
      };
      
      const response = await api.post('/mock-training/scenarios', scenarioData);
      setScenarios([...scenarios, response.data]);
      toast.success('Scenario created from incident');
    } catch (error) {
      toast.error('Failed to create scenario from incident');
    }
  };

  // Assignment handlers
  const openAssignDialog = (scenario) => {
    setSelectedScenarioForAssign(scenario);
    setSelectedUsers([]);
    setAssignmentDueDate('');
    setUserSearchQuery('');
    setShowAssignDialog(true);
  };

  // Filter team members by search query
  const filteredTeamMembers = teamMembers.filter(member => {
    if (!userSearchQuery.trim()) return true;
    const query = userSearchQuery.toLowerCase();
    const name = (member.name || '').toLowerCase();
    const email = (member.email || '').toLowerCase();
    return name.includes(query) || email.includes(query);
  });

  const handleAssignScenario = async () => {
    if (!selectedScenarioForAssign || selectedUsers.length === 0) {
      toast.error('Please select at least one team member');
      return;
    }

    setAssigningLoading(true);
    try {
      const assignmentData = {
        scenario_id: selectedScenarioForAssign.id,
        user_ids: selectedUsers,
        due_date: assignmentDueDate || null,
        tenant_id: tenant?.id
      };
      
      console.log('Creating mock training assignment:', assignmentData);

      await api.post('/mock-training/assignments', assignmentData);
      toast.success(`Scenario assigned to ${selectedUsers.length} team member(s)`);
      setShowAssignDialog(false);
      
      // Refresh assignments
      try {
        const assignmentsRes = await api.get('/mock-training/assignments');
        setAssignments(assignmentsRes.data.assignments || []);
      } catch (e) {
        console.log('Could not refresh assignments');
      }
      
      // Refresh stats
      try {
        const statsRes = await api.get('/mock-training/stats');
        setStats(statsRes.data || stats);
      } catch (e) {
        console.log('Could not refresh stats');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign scenario');
    } finally {
      setAssigningLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getCategoryColor = (category) => {
    const colors = {
      age: 'bg-blue-100 text-blue-700',
      pregnancy: 'bg-pink-100 text-pink-700',
      religion: 'bg-purple-100 text-purple-700',
      disability: 'bg-orange-100 text-orange-700',
      national_origin: 'bg-green-100 text-green-700',
      compliance: 'bg-red-100 text-red-700',
      general: 'bg-gray-100 text-gray-700'
    };
    return colors[category] || colors.general;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      hard: 'bg-red-100 text-red-700'
    };
    return colors[difficulty] || colors.medium;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mock-training-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Mock Training Center</h1>
          <p className="text-muted-foreground">
            Create and manage AI-powered mock conversations for recruiter training
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowAIGenerateDialog(true)}
            className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-200 hover:border-purple-300"
          >
            <Sparkles className="h-4 w-4 mr-2 text-purple-600" />
            AI Generate
          </Button>
          <Button variant="outline" onClick={() => setShowSettingsDialog(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Scenario
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="scenarios" className="gap-2">
            <Settings className="h-4 w-4" />
            Scenarios
          </TabsTrigger>
          <TabsTrigger value="assignments" className="gap-2">
            <Users className="h-4 w-4" />
            Assignments
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="gap-2">
            <Trophy className="h-4 w-4" />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="incidents" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            Real Incidents
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Scenarios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_scenarios || scenarios.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Assignments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.active_assignments}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Completed Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.completed_sessions}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Average Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.average_score?.toFixed(1) || 'N/A'}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Quick Start</CardTitle>
              <CardDescription>Get started with mock training</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div 
                  className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => setActiveTab('scenarios')}
                >
                  <Settings className="h-8 w-8 text-blue-600 mb-2" />
                  <h3 className="font-medium">Browse Scenarios</h3>
                  <p className="text-sm text-muted-foreground">View and manage training scenarios</p>
                </div>
                <div 
                  className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => setActiveTab('incidents')}
                >
                  <AlertTriangle className="h-8 w-8 text-orange-600 mb-2" />
                  <h3 className="font-medium">Real Incidents</h3>
                  <p className="text-sm text-muted-foreground">Create scenarios from actual calls</p>
                </div>
                <div 
                  className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="h-8 w-8 text-green-600 mb-2" />
                  <h3 className="font-medium">Create Scenario</h3>
                  <p className="text-sm text-muted-foreground">Build a new training scenario</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scenarios Tab */}
        <TabsContent value="scenarios" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Training Scenarios</CardTitle>
                  <CardDescription>Manage your mock training scenarios</CardDescription>
                </div>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Scenario
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {scenarios.length === 0 ? (
                <div className="text-center py-8">
                  <Settings className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-lg">No Scenarios Yet</h3>
                  <p className="text-muted-foreground mt-1">
                    Create your first scenario or load from real incidents
                  </p>
                  <div className="flex justify-center gap-2 mt-4">
                    <Button onClick={() => setShowCreateDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Scenario
                    </Button>
                    <Button variant="outline" onClick={() => setActiveTab('incidents')}>
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Load from Incidents
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {scenarios.map((scenario) => (
                    <div 
                      key={scenario.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{scenario.title}</h4>
                            <Badge className={getCategoryColor(scenario.category)}>
                              {scenario.category}
                            </Badge>
                            <Badge className={getDifficultyColor(scenario.difficulty)}>
                              {scenario.difficulty}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {scenario.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <Play className="h-4 w-4 mr-2" />
                          Preview
                        </Button>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-4">
          {/* Available Scenarios for Assignment */}
          <Card>
            <CardHeader>
              <CardTitle>Training Assignments</CardTitle>
              <CardDescription>Assign scenarios to team members</CardDescription>
            </CardHeader>
            <CardContent>
              {scenarios.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-lg">No Scenarios Available</h3>
                  <p className="text-muted-foreground mt-1">
                    Create scenarios first, then assign them to team members
                  </p>
                  <Button 
                    className="mt-4"
                    onClick={() => setActiveTab('scenarios')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Go to Scenarios
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground mb-3">
                    Available Scenarios ({scenarios.length})
                  </h4>
                  <div className="space-y-3">
                    {scenarios.map((scenario) => (
                      <div
                        key={scenario.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                        data-testid={`assign-scenario-${scenario.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{scenario.title}</h4>
                            <Badge className={getCategoryColor(scenario.category)}>
                              {scenario.category}
                            </Badge>
                            <Badge className={getDifficultyColor(scenario.difficulty)}>
                              {scenario.difficulty}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {scenario.description}
                          </p>
                        </div>
                        <Button
                          onClick={() => openAssignDialog(scenario)}
                          size="sm"
                          data-testid={`assign-btn-${scenario.id}`}
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Assign
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Existing Assignments */}
          {assignments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Active Assignments</CardTitle>
                <CardDescription>Track team member progress</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid={`assignment-${assignment.id}`}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium">{assignment.scenario_title || 'Training Scenario'}</h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {assignment.assigned_count || 1} assigned
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            {assignment.completed_count || 0} completed
                          </span>
                          {assignment.due_date && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Due: {new Date(assignment.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge variant={assignment.status === 'completed' ? 'default' : 'secondary'}>
                        {assignment.status || 'active'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Training Leaderboard</CardTitle>
              <CardDescription>Top performers in mock training</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium text-lg">No Rankings Yet</h3>
                <p className="text-muted-foreground mt-1">
                  Complete training sessions to appear on the leaderboard
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Real Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                    <CardTitle>Real Call Incidents</CardTitle>
                  </div>
                  <CardDescription>Create training scenarios from actual challenging calls</CardDescription>
                </div>
                <Button 
                  onClick={loadIncidents} 
                  disabled={loadingIncidents}
                  variant="outline"
                  data-testid="load-incidents-btn"
                >
                  {loadingIncidents ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Load Incidents
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!incidentsLoaded ? (
                <div className="text-center py-8">
                  <Phone className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    Click "Load Incidents" to fetch challenging calls
                  </p>
                </div>
              ) : incidents.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <h3 className="font-medium text-lg">No Challenging Calls Found</h3>
                  <p className="text-muted-foreground mt-1">
                    Great news! No calls with compliance issues were found.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {incidents.map((incident) => (
                    <div 
                      key={incident.id}
                      className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium">{incident.title}</h4>
                            <Badge className={getCategoryColor(incident.category)}>
                              {incident.violation_type || incident.category}
                            </Badge>
                            {incident.score && (
                              <Badge variant="outline" className={
                                incident.score >= 7 ? 'text-green-600' : 
                                incident.score >= 5 ? 'text-yellow-600' : 'text-red-600'
                              }>
                                Score: {incident.score}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {incident.description}
                          </p>
                          {incident.transcript_excerpt && (
                            <div className="text-xs text-muted-foreground bg-muted p-2 rounded mt-2">
                              <span className="font-medium">Excerpt: </span>
                              {incident.transcript_excerpt}
                            </div>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Recruiter: {incident.recruiter_name}</span>
                            <span>Date: {new Date(incident.date).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/dashboard/call/${incident.call_id}`)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Call
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => createScenarioFromIncident(incident)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Scenario
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Scenario Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Training Scenario</DialogTitle>
            <DialogDescription>
              Build a new mock training scenario for recruiter practice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Scenario Title</Label>
              <Input
                id="title"
                placeholder="e.g., Age-related disclosure handling"
                value={newScenario.title}
                onChange={(e) => setNewScenario({ ...newScenario, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={newScenario.category} 
                  onValueChange={(value) => setNewScenario({ ...newScenario, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="age">Age</SelectItem>
                    <SelectItem value="pregnancy">Pregnancy/Family</SelectItem>
                    <SelectItem value="religion">Religion</SelectItem>
                    <SelectItem value="disability">Disability</SelectItem>
                    <SelectItem value="national_origin">National Origin</SelectItem>
                    <SelectItem value="compliance">General Compliance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="difficulty">Difficulty</Label>
                <Select 
                  value={newScenario.difficulty} 
                  onValueChange={(value) => setNewScenario({ ...newScenario, difficulty: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the scenario..."
                value={newScenario.description}
                onChange={(e) => setNewScenario({ ...newScenario, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="candidate_statement">Candidate Statement</Label>
              <Textarea
                id="candidate_statement"
                placeholder="What the candidate will say that triggers the training scenario..."
                value={newScenario.candidate_statement}
                onChange={(e) => setNewScenario({ ...newScenario, candidate_statement: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ideal_response">Ideal Response</Label>
              <Textarea
                id="ideal_response"
                placeholder="The recommended way to handle this scenario..."
                value={newScenario.ideal_response}
                onChange={(e) => setNewScenario({ ...newScenario, ideal_response: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createScenario}>Create Scenario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mock Training Settings</DialogTitle>
            <DialogDescription>
              Configure mock training options
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground">Settings coming soon...</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSettingsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate Scenarios Dialog */}
      <Dialog open={showAIGenerateDialog} onOpenChange={setShowAIGenerateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI Scenario Generator
            </DialogTitle>
            <DialogDescription>
              Automatically generate training scenarios based on your team's common compliance issues
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Common Issues Preview */}
            {commonIssues.length > 0 && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">Your Team's Top Issues</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {commonIssues.slice(0, 5).map((issue, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {issue.type} ({issue.count})
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Number of Scenarios</Label>
              <Select 
                value={String(aiGenerateOptions.num_scenarios)} 
                onValueChange={(v) => setAiGenerateOptions({...aiGenerateOptions, num_scenarios: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select count" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Scenario</SelectItem>
                  <SelectItem value="3">3 Scenarios</SelectItem>
                  <SelectItem value="5">5 Scenarios</SelectItem>
                  <SelectItem value="10">10 Scenarios</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Categories to Focus On</Label>
              <div className="flex flex-wrap gap-2">
                {['age', 'pregnancy', 'religion', 'disability', 'national_origin'].map(cat => (
                  <Badge 
                    key={cat}
                    variant={aiGenerateOptions.categories.includes(cat) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer transition-colors",
                      aiGenerateOptions.categories.includes(cat) 
                        ? "bg-purple-600 hover:bg-purple-700" 
                        : "hover:bg-purple-100"
                    )}
                    onClick={() => {
                      const newCats = aiGenerateOptions.categories.includes(cat)
                        ? aiGenerateOptions.categories.filter(c => c !== cat)
                        : [...aiGenerateOptions.categories, cat];
                      setAiGenerateOptions({...aiGenerateOptions, categories: newCats});
                    }}
                  >
                    {cat.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch 
                id="use-real" 
                checked={aiGenerateOptions.use_real_incidents}
                onCheckedChange={(v) => setAiGenerateOptions({...aiGenerateOptions, use_real_incidents: v})}
              />
              <Label htmlFor="use-real" className="cursor-pointer">
                Analyze real incidents for context
              </Label>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <Zap className="h-4 w-4" />
                <span>AI will generate realistic scenarios based on EEOC compliance best practices</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIGenerateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateAIScenarios} 
              disabled={generatingAI || aiGenerateOptions.categories.length === 0}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              {generatingAI ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate Scenarios
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Scenario</DialogTitle>
            <DialogDescription>
              {selectedScenarioForAssign?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Team Members</Label>
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="user-search-input"
                />
              </div>
              {teamMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No team members found</p>
              ) : (
                <ScrollArea className="h-48 border rounded-lg p-2">
                  <div className="space-y-2">
                    {filteredTeamMembers.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No users match "{userSearchQuery}"
                      </p>
                    ) : (
                      filteredTeamMembers.map((member) => (
                        <div
                          key={member.id}
                          className={cn(
                            "flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                            selectedUsers.includes(member.id) && "bg-primary/10 border border-primary/20"
                          )}
                          onClick={() => toggleUserSelection(member.id)}
                          data-testid={`assign-user-${member.id}`}
                        >
                          <div className={cn(
                            "w-4 h-4 rounded border flex items-center justify-center",
                            selectedUsers.includes(member.id) 
                              ? "bg-primary border-primary" 
                              : "border-muted-foreground/30"
                          )}>
                            {selectedUsers.includes(member.id) && (
                              <CheckCircle className="h-3 w-3 text-primary-foreground" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{member.name || member.email}</p>
                            <p className="text-xs text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              )}
              {selectedUsers.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {selectedUsers.length} member(s) selected
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="due-date">Due Date (Optional)</Label>
              <Input
                id="due-date"
                type="date"
                value={assignmentDueDate}
                onChange={(e) => setAssignmentDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignScenario}
              disabled={assigningLoading || selectedUsers.length === 0}
              data-testid="confirm-assign-btn"
            >
              {assigningLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  Assign to {selectedUsers.length} Member(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
